import { ReactNode } from 'react';

import { Copy, MoreVertical, Pencil, Star, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { SimpleTooltip } from '@/components/ui/simple-tooltip';
import { TruncatedTooltip } from '@/components/ui/truncated-tooltip';

import { UserGroupPermissions } from '../../types/permission';

interface GroupMenuItemProps {
  /** Why the action is unavailable. Undefined means the item is enabled. */
  disabledReason?: string;
  className?: string;
  onSelect: () => void;
  children: ReactNode;
}

/**
 * Menu item that explains on hover why it is unavailable, instead of vanishing.
 *
 * A disabled item has `pointer-events: none`, so it can never be the tooltip trigger —
 * the wrapping span is.
 */
function GroupMenuItem({ disabledReason, className, onSelect, children }: GroupMenuItemProps) {
  const item = (
    <DropdownMenuItem disabled={Boolean(disabledReason)} className={className} onSelect={onSelect}>
      {children}
    </DropdownMenuItem>
  );

  if (!disabledReason) return item;

  return (
    <SimpleTooltip
      content={<p className="leading-relaxed">{disabledReason}</p>}
      contentClassName="max-w-xs"
    >
      <span className="flex cursor-not-allowed">{item}</span>
    </SimpleTooltip>
  );
}

interface UserGroupColumnHeaderProps {
  group: UserGroupPermissions;
  onRename: (group: UserGroupPermissions) => void;
  onDuplicate: (group: UserGroupPermissions) => void;
  onDelete: (group: UserGroupPermissions) => void;
  onSetAsDefault: (group: UserGroupPermissions) => void;
  /** Translated reason the group cannot be renamed; undefined when it can. */
  renameDisabledReason?: string;
  /** Translated reason the group cannot be deleted; undefined when it can. */
  deleteDisabledReason?: string;
  /** Translated reason the group cannot become the default; undefined when it can. */
  setAsDefaultDisabledReason?: string;
}

/**
 * Header for a single user group column: the group name plus the actions menu.
 *
 * The caller derives every disabled reason and passes it in. Rename, Delete and Set as default
 * stay visible when unavailable and explain themselves on hover — Super Admin is protected from
 * all three, a delete is also blocked while too few groups remain, and the current default has
 * nothing to set. Duplicate is never gated: copying any group, Super Admin included, is allowed.
 *
 * The default group is marked with a filled star beside its name, matching the icon on the menu
 * action. It has to stay on the same single row as the name: `th` is `align-middle`, so a second
 * row would make this one cell taller than the rest and every other column's label — including the
 * pinned "Permission" header — would re-center against it. A text badge does not fit either; at
 * this column width it would leave too little room for the name.
 */
export function UserGroupColumnHeader({
  group,
  onRename,
  onDuplicate,
  onDelete,
  onSetAsDefault,
  renameDisabledReason,
  deleteDisabledReason,
  setAsDefaultDisabledReason,
}: UserGroupColumnHeaderProps) {
  const { t } = useTranslation('authenticated.settings');

  return (
    <div className="flex w-full min-w-0 items-center justify-center gap-0.5">
      {/* Name and star centre together as one unit. TruncatedTooltip renders `w-full`, so the
          override to `w-auto` is what makes the box hug the text and keeps the star beside it
          instead of pushed out to the menu button. */}
      <div className="flex min-w-0 flex-1 items-center justify-center gap-1">
        <TruncatedTooltip content={group.name} className="w-auto min-w-0 font-semibold" />

        {group.isDefault && (
          <SimpleTooltip content={<p>{t('pages.permissions.defaultGroupHint')}</p>}>
            <span className="flex shrink-0 items-center">
              <Star
                className="h-3.5 w-3.5 fill-current text-primary"
                aria-label={t('pages.permissions.defaultGroupHint')}
              />
            </span>
          </SimpleTooltip>
        )}
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          {/* type="button": this header renders inside the page form */}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6 shrink-0"
            aria-label={t('pages.permissions.groupMenu.ariaLabel', { name: group.name })}
          >
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <GroupMenuItem disabledReason={renameDisabledReason} onSelect={() => onRename(group)}>
            <Pencil className="mr-2 h-4 w-4" />
            {t('pages.permissions.groupMenu.rename')}
          </GroupMenuItem>
          <DropdownMenuItem onSelect={() => onDuplicate(group)}>
            <Copy className="mr-2 h-4 w-4" />
            {t('pages.permissions.groupMenu.duplicate')}
          </DropdownMenuItem>
          <GroupMenuItem
            disabledReason={setAsDefaultDisabledReason}
            onSelect={() => onSetAsDefault(group)}
          >
            <Star className="mr-2 h-4 w-4" />
            {t('pages.permissions.groupMenu.setAsDefault')}
          </GroupMenuItem>
          <GroupMenuItem
            disabledReason={deleteDisabledReason}
            className="text-destructive focus:text-destructive"
            onSelect={() => onDelete(group)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            {t('pages.permissions.groupMenu.delete')}
          </GroupMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}
